import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, BookOpen, ArrowLeft, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import AppShell from '@/components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const { grade } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const gradeLabels = {
    7: 'Kansas History (7th Grade)',
    8: 'U.S. History (8th Grade)',
    9: 'World History',
    10: 'Modern World History',
    11: 'United States History',
    12: 'Government & Economics'
  };

  useEffect(() => {
    fetchUnits();
  }, [grade]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = units.filter(unit =>
        unit.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        unit.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUnits(filtered);
    } else {
      setFilteredUnits(units);
    }
  }, [searchQuery, units]);

  const fetchUnits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/units/grade/${grade}`);
      setUnits(response.data);
      setFilteredUnits(response.data);
    } catch (error) {
      toast.error('Failed to load curriculum units');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        {/* Page header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6" data-testid="grade-dashboard-header">
          <div>
            <button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-1 text-xs text-[#7A93AC] hover:text-[#14213D] mb-2" data-testid="back-to-dashboard">
              <ArrowLeft className="h-3 w-3" /> All grades
            </button>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-1">Grade {grade}</p>
            <h1 className="font-serif-display text-3xl sm:text-4xl text-[#14213D] tracking-tight">{gradeLabels[grade] || 'Curriculum'}</h1>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/syllabus/${grade}`)}
                className="border-[#14213D] text-[#14213D] hover:bg-[#F9F8F4]"
                data-testid="grade-syllabus-btn"
                title="Generate a full-year course syllabus for this grade"
              >
                <GraduationCap className="h-4 w-4 mr-2" /> Syllabus
              </Button>
              <Button
                onClick={() => navigate('/create', { state: { gradeLevel: parseInt(grade) } })}
                className="bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7]"
                data-testid="create-unit-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> New Unit
              </Button>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6577]" />
            <Input
              type="text"
              placeholder="Filter units in this grade…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#F9F8F4] border-[#EBE7E0] focus-visible:ring-[#14213D]/30"
              data-testid="search-units-input"
            />
          </div>
        </div>

        {/* Units Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent"></div>
            <p className="mt-4 text-sm text-[#5A6577]">Loading curriculum units…</p>
          </div>
        ) : filteredUnits.length === 0 ? (
          <div className="text-center py-20" data-testid="no-units-message">
            <BookOpen className="h-12 w-12 text-[#E2E8F0] mx-auto mb-4" />
            <h3 className="font-serif-display text-xl text-[#14213D] mb-2">No units found</h3>
            <p className="text-sm text-[#5A6577] mb-6">
              {searchQuery ? 'Try adjusting your search query' : 'Start by creating your first curriculum unit'}
            </p>
            {!searchQuery && isAdmin && (
              <Button
                onClick={() => navigate('/create', { state: { gradeLevel: parseInt(grade) } })}
                className="bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7]"
                data-testid="create-first-unit-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> Create first unit
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="units-grid">
            {filteredUnits.map((unit) => (
              <article
                key={unit.id}
                data-testid={`unit-card-${unit.id}`}
                className="group bg-white border border-[#EBE7E0] rounded-md p-5 hover:-translate-y-1 hover:shadow-md hover:border-[#14213D]/20 cursor-pointer transition-all relative overflow-hidden"
                onClick={() => navigate(`/unit/${unit.id}`)}
              >
                <div className="absolute top-0 left-0 w-full h-0.5 bg-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
                <p className="text-[11px] uppercase tracking-[0.15em] text-[#D4AF37] font-semibold mb-2">Unit {unit.unit_number}</p>
                <h3 className="font-serif-display text-lg text-[#14213D] mb-3 leading-tight line-clamp-2">{unit.title}</h3>
                <p className="text-sm text-[#5A6577] leading-relaxed line-clamp-3 mb-4">{unit.description}</p>
                <div className="flex flex-wrap gap-2 pt-3 border-t border-[#EBE7E0]">
                  {unit.kansas_standards?.length > 0 && (
                    <span className="text-[11px] bg-[#E2E8F0]/60 text-[#2D3748] px-2 py-1 rounded">{unit.kansas_standards.length} Standards</span>
                  )}
                  {unit.vocabulary?.length > 0 && (
                    <span className="text-[11px] bg-[#E2E8F0]/60 text-[#2D3748] px-2 py-1 rounded">{unit.vocabulary.length} Terms</span>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
